/**
 * KisiIletisimBilgileri — the company's Personel Detay › Detay Bilgileri: a worker's phone and e-mail, editable in place (backlog #749; backend #747).
 *
 * The company sends PATCH /personnel/{id}/kunye with ONLY the changed key (telefon | eposta):
 *   · 200: written at once, the new value is shown;
 *   · 202 (`kod` DUZENLEME_TALEBI): the card is locked (KVKK accepted, Kimlik Doğrulandı, or a verified phone) — nothing was written, the change
 *     became an Edit Kişi Request staff decide; the reasons (`kilitSebepleri`) are told and the request shows under the fields with "Geri çek".
 * The newest request for the kişi (GET /kisi-duzenleme-talepleri?personId=) is read when the card opens: a pending one waits for SAKA, a rejected one
 * tells the reason (`redSebebi`). Any other newest state (approved, withdrawn) shows nothing — the field already shows the card's value.
 * The change goes through kunyeyiGonder: the company's earlier pending request for the person is replaced (not merged) by the backend, so its other
 * field rides along in the new PATCH (a second change must not withdraw the first). The phone is edited with the shared TelefonGirdisi (#746: country code + national number, one stored-form value), the e-mail with a plain box.
 * `yazabilir` = PERSONEL WRITE and the relationship is not ended.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Input, Row, Spinner } from 'reactstrap';
import { Mail, Pencil, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import TelefonGirdisi from '../Common/TelefonGirdisi';
import { kunyeyiGonder } from '../../services/kisiKunyeGonder';
import {
  geriCekTalep, getGosterilecekTalep, talepGonderildi, talepHatasi, TALEP_DURUMU,
} from '../../services/kisiDuzenlemeTalebiService';
import { kilitMetni } from './duzenlemeTalebi';

const ALANLAR = [
  { alan: 'telefon', Icon: Phone },
  { alan: 'eposta', Icon: Mail },
];

export default function KisiIletisimBilgileri({ personId, telefon, eposta, yazabilir = false }) {
  const { t } = useTranslation('components');
  const { t: tc } = useTranslation('common');
  const [yerel, setYerel] = useState({});       // a value just written (200), over the page's own
  const [talep, setTalep] = useState(null);     // the request to tell about (pending / rejected)
  const [duzen, setDuzen] = useState(null);     // 'telefon' | 'eposta' | null
  const [girdi, setGirdi] = useState('');
  const [calisiyor, setCalisiyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [bilgi, setBilgi] = useState(null);

  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const k = await getGosterilecekTalep(personId);
        if (!ignore) setTalep(k);
      } catch {
        if (!ignore) setTalep(null); // the card is still usable without the note
      }
    }
    if (personId) run();
    return () => { ignore = true; };
  }, [personId]);

  const degerler = { telefon: yerel.telefon ?? telefon, eposta: yerel.eposta ?? eposta };

  function ac(alan) {
    setDuzen(alan);
    setGirdi(degerler[alan] || '');
    setHata(null);
    setBilgi(null);
  }

  async function kaydet(e) {
    e.preventDefault();
    const yeni = girdi.trim();
    if (!yeni || yeni === (degerler[duzen] || '')) return;
    setCalisiyor(true);
    setHata(null);
    try {
      const sonuc = await kunyeyiGonder(personId, { [duzen]: yeni });
      if (talepGonderildi(sonuc)) {
        // The request card below says it all (waiting, the change, why); the backend's one-line message would only repeat it.
        setTalep(sonuc.talep || null);
        setBilgi(null);
      } else if (Array.isArray(sonuc.degisenAlanlar) && sonuc.degisenAlanlar.length === 0) {
        setBilgi(sonuc.message || t('duzenlemeTalebi.degisiklikYok'));
      } else {
        // The answer is the whole künye: a field that rode along from the pending request was written too.
        setYerel((y) => ({ ...y, telefon: sonuc.telefon ?? y.telefon, eposta: sonuc.eposta ?? y.eposta, [duzen]: sonuc[duzen] ?? yeni }));
        setBilgi(t('duzenlemeTalebi.kaydedildi'));
        getGosterilecekTalep(personId).then(setTalep).catch(() => {}); // a pending request that rode along is withdrawn by kunyeyiGonder
      }
      setDuzen(null);
    } catch (err) {
      setHata((err.hatalar && err.hatalar[duzen]) || talepHatasi(err));
    } finally {
      setCalisiyor(false);
    }
  }

  async function geriCek() {
    setCalisiyor(true);
    setHata(null);
    try {
      const r = await geriCekTalep(talep.id);
      setTalep(null);
      setBilgi(r.mesaj || t('duzenlemeTalebi.geriCekildi'));
    } catch (err) {
      setHata(talepHatasi(err));
    } finally {
      setCalisiyor(false);
    }
  }

  const bekliyor = talep && talep.durum === TALEP_DURUMU.BEKLIYOR;
  const degisimler = talep ? [
    talep.telefonDegisir && { alan: 'telefon', eski: talep.eskiTelefon, yeni: talep.yeniTelefon },
    talep.epostaDegisir && { alan: 'eposta', eski: talep.eskiEposta, yeni: talep.yeniEposta },
  ].filter(Boolean) : [];

  return (
    <div data-testid="kisi-iletisim">
      <Row className="g-3">
        {ALANLAR.map(({ alan, Icon }) => (
          <Col key={alan} md={4} sm={6}>
            <div className="text-muted small d-flex align-items-center gap-1"><Icon size={12} />{t(`duzenlemeTalebi.${alan}`)}</div>
            {duzen === alan ? (
              <form onSubmit={kaydet} className="mt-1" data-testid={`iletisim-form-${alan}`}>
                {alan === 'telefon' ? (
                  <TelefonGirdisi bsSize="sm" id="iletisim-girdi-telefon" value={girdi} onChange={setGirdi} />
                ) : (
                  <Input
                    bsSize="sm" type="email" maxLength={100} autoFocus value={girdi} onChange={(e) => setGirdi(e.target.value)}
                    aria-label={t('duzenlemeTalebi.eposta')} id="iletisim-girdi-eposta"
                  />
                )}
                <div className="d-flex gap-1 mt-1">
                  <Button color="primary" size="sm" type="submit" disabled={calisiyor || !girdi.trim()} data-testid={`iletisim-kaydet-${alan}`}>
                    {calisiyor ? <Spinner size="sm" /> : tc('kaydet')}
                  </Button>
                  <Button color="light" size="sm" type="button" disabled={calisiyor} onClick={() => setDuzen(null)}>{tc('iptal')}</Button>
                </div>
              </form>
            ) : (
              <div className="fw-semibold text-break">
                <span data-testid={`iletisim-deger-${alan}`}>{degerler[alan] || '—'}</span>
                {yazabilir && (
                  <Button
                    color="link" size="sm" className="p-0 ms-2 align-baseline text-muted" onClick={() => ac(alan)}
                    title={tc('duzenle')} aria-label={`${tc('duzenle')}: ${t(`duzenlemeTalebi.${alan}`)}`} data-testid={`iletisim-duzenle-${alan}`}
                  >
                    <Pencil size={13} />
                  </Button>
                )}
              </div>
            )}
          </Col>
        ))}
      </Row>

      {hata && <Alert color="danger" fade={false} className="py-2 mt-3 mb-0" data-testid="iletisim-hata">{hata}</Alert>}
      {bilgi && <Alert color="success" fade={false} className="py-2 mt-3 mb-0" data-testid="iletisim-bilgi">{bilgi}</Alert>}
      {talep && (
        <Alert color={bekliyor ? 'info' : 'warning'} fade={false} className="py-2 mt-3 mb-0" data-testid="iletisim-talep">
          <div className="fw-semibold">{bekliyor ? t('duzenlemeTalebi.bekliyor') : t('duzenlemeTalebi.reddedildi')}</div>
          {degisimler.map((d) => (
            <div key={d.alan} className="small">{t('duzenlemeTalebi.degisim', { alan: t(`duzenlemeTalebi.${d.alan}`), eski: d.eski || '—', yeni: d.yeni || '—' })}</div>
          ))}
          {bekliyor && (talep.kilitSebepleri || []).length > 0 && (
            <div className="small">{t('duzenlemeTalebi.sebep', { sebepler: kilitMetni(t, talep.kilitSebepleri) })}</div>
          )}
          {!bekliyor && talep.redSebebi && <div className="small" data-testid="iletisim-red-sebebi">{t('duzenlemeTalebi.redSebebi', { sebep: talep.redSebebi })}</div>}
          {bekliyor && yazabilir && (
            <Button color="link" size="sm" className="p-0 mt-1" disabled={calisiyor} onClick={geriCek} data-testid="iletisim-geri-cek">{t('duzenlemeTalebi.geriCek')}</Button>
          )}
        </Alert>
      )}
    </div>
  );
}
